import React, { useCallback } from "react";
import classNames from "classnames";
import { Controlled as ControlledZoom } from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

import FileCard from "./FileCard";
import VideoPlayer from "./VideoPlayer";
import { isImageOrVideo } from "@qoohoo/common/utils/url";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { button } from "@qoohoo/common/constants/analytics";
import useScreenSize from "@qoohoo/common/hooks/useScreenSize";

export default function MediaViewer({
  url,
  currentSlide,
  mediaFileName = "qoohoo-asset",
  mediaFileSize = 0,
  trackImageClick,
  trackVideoClick,
  imageClassName = "",
  videoClassName = "",
  videoPlayerClassName = "",
  channelName="",
  creatorName="",
  showWatermark = false,
}) {
  const screenSize = useScreenSize();
  const [isZoomed, setIsZoomed] = React.useState(false);

  const isSmallScreen = screenSize === "xs" || screenSize === "sm";
  const videoSrc = {
    type: "video",
    title: "qoohoo",
    sources: [
      {
        src: url,
        type: "video/mp4",
      },
    ],
  };

  const type = isImageOrVideo(url);

  const handleZoomChange = useCallback((shouldZoom) => {
    setIsZoomed(shouldZoom);

    if (typeof trackImageClick === "function" && shouldZoom) {
      trackImageClick();
    }
  }, []);

  function trackAudioPlayPauseEvent(event) {
    if (event === "play") {
      Track.buttonClick(button.play_audio, {
        url,
      });
    } else if (event === "pause") {
      Track.buttonClick(button.pause_audio, {
        url,
      });
    }
  }

  if (type === "img")
    return (
      <div
        className={classNames(
          "flex items-center justify-center rounded-xl overflow-hidden max-h-full max-w-full",
          imageClassName
        )}
      >
        <ControlledZoom
          isZoomed={isZoomed}
          onZoomChange={handleZoomChange}
          overlayBgColorEnd="#19191d"
          overlayBgColorStart="#19191d"
          zoomMargin={isSmallScreen ? 0 : 50}
          wrapStyle={
            !isZoomed
              ? {
                  width: "100%",
                  height: "100%",
                }
              : null
          }
        >
          <img
            src={url}
            alt="channel media"
            className={classNames(
              "!w-full !h-full select-none",
              isZoomed ? "object-contain" : "object-cover"
            )}
          />
        </ControlledZoom>
      </div>
    );
  else if (type === "video") {
    return (
      <div
        key={url}
        id="player"
        className={classNames(
          "relative object-contain bg-center rounded-xl !h-auto children:w-full children:rounded-2xl flex items-center justify-center outline-none z-[1] overflow-hidden",
          videoClassName
        )}
      >
        <VideoPlayer
          sources={videoSrc.sources}
          currentSlide={currentSlide}
          videoPlayerClassName={videoPlayerClassName}
          trackVideoClick={trackVideoClick}
          channelName={channelName}
          creatorName={creatorName}
          showWatermark={showWatermark}
        />
      </div>
    );
  } else if (type === "audio") {
    return (
      <div key={url}>
        <audio
          src={url}
          className="w-full"
          controls
          controlsList="nodownload"
          onPlay={() => {
            trackAudioPlayPauseEvent("play");
          }}
          onPause={() => {
            trackAudioPlayPauseEvent("pause");
          }}
        >
          Your browser doesn&apos;t support this audio. Please open in app.
        </audio>
      </div>
    );
  } else {
    return (
      <FileCard
        url={url}
        mediaFileName={mediaFileName}
        mediaFileSize={mediaFileSize}
      />
    );
  }
}
